import { z } from 'zod';

import { ReschedulePolicyCutoffUnit } from './types';
import { LocalDateTimeStringSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { HostSchema, makeEndpoint } from '../common';
import { SessionStatus } from '../event/sessions';
import { PriceTierSchema } from '../experience/price-tier-rules';
import { ExperienceSchema } from '../resource/common';
import { ResourceGroupResponseSchema } from '../resource/resource-group';
import { ResourceGroupCollectionResponseSchema } from '../resource/resource-group-collection';

export const ReschedulePolicySchema = z.object({
  id: z.string().uuid(),
  active: z.boolean(),
  cutoffValue: z.number().min(0),
  cutoffUnit: z.nativeEnum(ReschedulePolicyCutoffUnit),
  notes: z.string().nonempty(),
});

const CreateReschedulePolicyRequestSchema = ReschedulePolicySchema.omit({ id: true });
const CreateReschedulePolicyResponseSchema = ReschedulePolicySchema.pick({ id: true });

const GetReschedulePolicyResponseSchema = ReschedulePolicySchema;
export type GetReschedulePolicyResponse = z.infer<typeof GetReschedulePolicyResponseSchema>;

export enum ReschedulableStatus {
  ALLOWED = 'ALLOWED',
  NOT_ALLOWED = 'NOT_ALLOWED',
}

export const ReschedulableBookingSchema = z.object({
  bookingId: z.string().uuid(),
  coverMedia: z.any(),
  customerId: z.string().uuid(),
  experienceTitle: z.string().nullable(),
  eventStartDateTime: z.date(),
  eventSessionDuration: z.any(),
  eventSessionDurationInMinutes: z.number(),
  experienceTimeZoneId: z.string().nullable(),
  experienceMediaIds: z.array(z.string().uuid()),
  reschedulePolicyId: z.string().uuid().nullable(),
  reschedulePolicyNotes: z.string().nullable(),
  reschedulePolicyActive: z.boolean().nullable(),
  reschedulePolicyCutoffUnit: z.nativeEnum(ReschedulePolicyCutoffUnit).nullable(),
  reschedulePolicyCutoffValue: z.number().nullable(),
  status: z.nativeEnum(ReschedulableStatus),
});

export const ReschedulableBookingsSchema = z.array(ReschedulableBookingSchema).describe('List of reschedulable bookings');

const GetBookingsForGuestRescheduleResponseSchema = z
  .array(ReschedulableBookingSchema)
  .describe('List the bookings associated to a cart id for guest cancellation.');

export type ReschedulableBooking = z.output<typeof ReschedulableBookingSchema>;

const GetBookingsForGuestRescheduleQuerySchema = z.object({
  token: z.string().nonempty(),
  languageCode: z.string().optional(),
});

export const GetBookingsForGuestRescheduleEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    cartId,
    query,
  }: {
    brandId: string;
    cartId: string;
    query: z.input<typeof GetBookingsForGuestRescheduleQuerySchema>;
  }) => `v1/brands/${brandId}/carts/${cartId}/guest-reschedule?${buildQueryString(query)}`,
  querySchema: GetBookingsForGuestRescheduleQuerySchema,
  requestSchema: z.void(),
  responseSchema: GetBookingsForGuestRescheduleResponseSchema,
});

export const CreateReschedulePolicyEndpoint = makeEndpoint({
  title: 'New reschedule policy',
  description: 'Create a new reschedule policy for a brand.',
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/reschedule-policies`,
  requestSchema: CreateReschedulePolicyRequestSchema,
  responseSchema: CreateReschedulePolicyResponseSchema,
});

export const GetReschedulePolicyEndpoint = makeEndpoint({
  method: 'GET',
  description: 'Get a reschedule policy by ID.',
  url: ({ brandId, reschedulePolicyId }: { brandId: string; reschedulePolicyId: string }) =>
    `v1/brands/${brandId}/reschedule-policies/${reschedulePolicyId}`,
  requestSchema: z.void(),
  responseSchema: GetReschedulePolicyResponseSchema,
});

const GetSessionsToRescheduleQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  allowOverbooking: z.string().optional(),
});

export const SessionsToReschedule = z.array(
  z.object({
    sessionId: z.string().uuid().nullable(),
    scheduleId: z.string().uuid().nullable(),
    isRepeating: z.boolean(),
    isPrivateEvent: z.boolean(),
    priceTiers: z.array(PriceTierSchema),
    startDateTime: LocalDateTimeStringSchema,
    sessionDuration: z.number().int().positive(),
    purchaserName: z.string().optional(),
    participantCount: z.number().int().nonnegative(),
    isFullyCheckedIn: z.boolean(),
    capacity: z.number().nullable(), // open experiences do not have a capacity
    experienceId: z.string().uuid(),
    overlappedEvent: z.boolean().optional(),
    status: z.nativeEnum(SessionStatus),
    hostedBy: HostSchema.nullable(),
    isBookableViaNegativeCutOffHours: z.boolean(),
  }),
);

export const GetSessionsToRescheduleResourceGroupCollection = ResourceGroupCollectionResponseSchema.extend({
  resourceGroups: z.array(ResourceGroupResponseSchema),
});

export const GetSessionsToRescheduleResponseSchema = z.object({
  sessions: SessionsToReschedule,
  experiences: z.array(ExperienceSchema),
  experienceId: z.string().uuid(), // The ID of the experience used to query sessions
  resourceGroupCollections: z.array(GetSessionsToRescheduleResourceGroupCollection),
});

export const GetSessionsToRescheduleEndpoint = makeEndpoint({
  title: 'All sessions to reschedule a booking',
  description: 'It returns all the sessions that are eligible for a rebook scenario.',
  method: 'GET',
  url: ({ bookingId, query }: { bookingId: string; query: z.infer<typeof GetSessionsToRescheduleQuerySchema> }) =>
    `v1/bookings/${bookingId}/sessions-to-reschedule?${buildQueryString(query)}`,
  querySchema: GetSessionsToRescheduleQuerySchema,
  requestSchema: z.void(),
  responseSchema: GetSessionsToRescheduleResponseSchema,
});
